// RGR10 반품모니터링 — moms-be 계약 타입.
// BE: ReturnMonitoringController(조회·요약·다운로드·반품철회) / ReturnMonitoringSaveController(저장)

/**
 * 화주 식별자 축 — `shipperCode`(화주코드), 화주명이 아니다.
 *
 * 근거(코드 3건 대조, dev DB 실측 전 QueryPie 다운으로 임시 확정 — TODO(verify) 아래 참고):
 *  1. moms-be `User.kt` — `MOMS_USER_INFO.SPR_NM` 컬럼이 Kotlin 필드 `shipperCode` 로 매핑된다.
 *  2. moms-be `ReturnMonitoringSearchJpaRepository.commonPredicate` — 쿼리 파라미터 `sprNm` 이
 *     `returnMonitoringSearch.shipperCode.eq(sprNm)` 로 바인딩된다(엔티티 필드명 자체가 shipperCode).
 *  3. moms-fe-1.0 `RGR10Container.js` — `SPR_NM: JSON.parse(sessionStorage.getItem('userInfo')).company` 로
 *     보내는 값이 2.0 `AuthStorage.user.get().company`(= 로그인 응답 `SPR_NM`)와 같은 값이고,
 *     이는 GR05(`allInboundStatus`)가 이미 화주코드로 검증해 쓰는 `AuthStorage.user.get().shipperCode` 와 동일 출처다.
 *
 * // TODO(verify): SPR_NM 축은 moms-be 쿼리·1.0 컨테이너 근거로 확정. QueryPie dev 복구 후 실측 대조 필요.
 */
export type ReturnMonitoringDateType = 'RTN_DATE' | 'ACT_RETURN_DATE' | 'INSPECT_DATE' | 'GR_ACT_DATE'

/**
 * RGR10 1.0 동등성 전환 플래그 2종(LOGISPRD-12602, 근거 LOGISPRD-12578 D-04·D-06).
 * 조회(읽기)와 반품철회(쓰기)는 위험도가 달라 키를 따로 둔다 — 한 키로 묶지 않는다.
 */

/** 조회 기준일 선택 노출. off 면 칸이 뜨지 않고 조회 요청 `dateType` 은 `RTN_DATE` 고정이다. */
export const RETURN_MONITORING_DATE_TYPE_FILTER_FLAG = 'feature.returnMonitoring.dateTypeFilter' as const

/** 반품철회 부분 처리. off 면 접수완료가 아닌 행이 하나라도 섞이면 예전처럼 전량 차단한다. */
export const RETURN_MONITORING_PARTIAL_WITHDRAW_FLAG = 'feature.returnMonitoring.partialWithdraw' as const

/** 조회·요약·다운로드 공통 필터 8종 — 쿼리 파라미터 이름 그대로(`ReturnMonitoringController`). */
export interface ReturnMonitoringSearchReq {
  dateType?: ReturnMonitoringDateType
  /** yyyyMMdd */
  dateFrom?: string
  /** yyyyMMdd */
  dateTo?: string
  /** 화주코드(SPR_NM) 완전일치 */
  shipper?: string
  rtnInvoiceNo?: string[]
  actFlag?: boolean
  ordNo?: string[]
  /** 상태 탭. '전체' 는 서버가 필터 미적용으로 처리한다. */
  ordStatus?: string
}

/**
 * 조회 응답 항목 — 화면이 실제로 그리는 40필드만 옮긴다(BE DTO 는 60필드).
 * `WH_NM`(입고센터)은 2.0 DTO 에 대응 필드가 없다 — TODO(api), 4장 참고.
 */
export interface ReturnMonitoringItemDto {
  id: number | null
  sprNm: string | null
  ordStatus: string | null
  clsDate: string | null
  rtnDate: string | null
  receiptDate: string | null
  actReturnDate: string | null
  grActDate: string | null
  inspectDate: string | null
  recStartDate: string | null
  orgInvoiceNo: string | null
  rtnInvoiceNo: string | null
  barcode: string | null
  skuId: string | null
  rtnQty: number | null
  carrier: string | null
  claimType: string | null
  customerClaim: string | null
  centerClaim: string | null
  etc1: string | null
  companyOrdNo: string | null
  companyOrdOptNo: string | null
  refOrdNo: string | null
  refOrdOptNo: string | null
  rtnWhNm: string | null
  actFlag: boolean | null
  giOrdNo: string | null
  giOrdOptNo: string | null
  giWhNm: string | null
  giActDate: string | null
  brdNm: string | null
  gdsNm: string | null
  gdsOpt: string | null
  trsNm: string | null
  trsAddr: string | null
  trsPhone: string | null
  insBy: string | null
  insDate: string | null
  updBy: string | null
  rtnExcDiv: string | null
}

/** `GET /api/v1/return/monitoring/summary` 응답 행. RETURN_STATUS 콤보 10종이 항상 온다. */
export interface ReturnMonitoringStatusSummaryDto {
  ordStatus: string
  totalCount: number
}

/** `POST /api/v1/return/monitoring/download` 요청 — 필터 8종 + 사유·비밀번호. */
export type ReturnMonitoringDownloadReq = ReturnMonitoringSearchReq & {
  password: string
  detailedReason: string
}

/** 저장 행 — patch 필드는 3상태(키 부재=미변경/`null`=삭제/값=갱신)다. `edited='I'` 는 patch 규약을 쓰지 않는다. */
export interface ReturnMonitoringSaveRowRequest {
  edited: 'I' | 'E'
  id?: number
  /** 행 화주코드 — 비면 최상위 `shipperCode`. 하나라도 쓰기 범위 밖이면 403, 저장 0건. */
  shipperCode?: string
  clsDate?: string
  giRefNo1?: string
  giRefNo2?: string
  orgInvoiceNo?: string | null
  barcode?: string | null
  skuId?: string
  rtnQty?: number | null
  carrier?: string | null
  rtnInvoiceNo?: string | null
  claimId?: string | null
  claimType?: string | null
  customerClaim?: string | null
  centerClaim?: string | null
  receiptDate?: string | null
  ordRemark?: string | null
  etc1?: string | null
  etc2?: string | null
  companyOrdNo?: string | null
  companyOrdOptNo?: string | null
  refOrdNo?: string | null
  refOrdOptNo?: string | null
  rtnWhNm?: string | null
}

export interface ReturnMonitoringSaveReq {
  shipperCode: string
  rows: ReturnMonitoringSaveRowRequest[]
}

export interface ReturnMonitoringSaveRes {
  totalCount: number
  successCount: number
  failureCount: number
  createdCount: number
  updatedCount: number
  duplicateOrgInvoiceNos: string[]
  sourceNotFound: string[]
  message: string
}

export interface ReturnMonitoringWithdrawReq {
  shipperCode: string
  ids: number[]
}

export interface ReturnMonitoringWithdrawRes {
  affectedRows: number
}
